import { Platform } from '@ionic/angular';
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ConfigService } from '../config/config.service';
import { SseService } from '../sse.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

const TOKEN_KEY = 'auth-token';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  authenticationState = new BehaviorSubject(false);
  private isLoggingOut = false;
  private resolveReady: (value: boolean) => void;
  public ready: Promise<boolean>;

  public userActions: string;

  constructor(
    private storage: Storage,
    private plt: Platform,
    public http: HttpClient,
    public configService: ConfigService,
    private sseService: SseService,
    private router: Router,
  ) {
    this.ready = new Promise((resolve) => {
      this.resolveReady = resolve;
    });
    this.plt.ready().then(() => {
      this.checkToken();
    });
  }

  checkToken() {
    this.storage.get(TOKEN_KEY).then((res) => {
      if (res) {
        this.authenticationState.next(true);
        this.resolveReady(true);
      } else {
        this.resolveReady(false);
      }
    }).catch(() => {
      this.resolveReady(false);
    });
  }

  login(username: string, password: string, appName: string): Observable<any> {
    return from(this.configService.getBaseUrl()).pipe(
      switchMap((baseUrl) =>
        from(this.configService.getFromLocation()).pipe(
          switchMap((companyName) => {
            const body = {
              username,
              password,
              companyName,
              appName,
            };

            return this.http.post<any>(
              baseUrl + 'user-service/api/user/login',
              body,
            );
          }),
        ),
      ),
      tap(async (response) => {
        const responseData = response.data;

        this.userActions = responseData.listOfActions;

        const token = responseData.token_type + ' ' + responseData.access_token;

        await this.storage.set(TOKEN_KEY, token);
        responseData.scanBoardUrl = 'http://127.0.0.1:9000/api/';
        await this.storage.set('userData', responseData);
        await this.storage.set('companyId', responseData.companyId);
        await this.storage.set('username', responseData.username);

        this.authenticationState.next(true);
      }),
      map((response) => response.data),
    );
  }

  async logout() {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;

    await this.storage.clear(); // 👈 safest

    await this.sseService.unsubscribeSSE();

    this.authenticationState.next(false);

    // 🔥 force redirect
    this.router.navigateByUrl('/login', { replaceUrl: true });

    setTimeout(() => {
      this.isLoggingOut = false;
    }, 0);
  }


  isAuthenticated() {
    return this.authenticationState.value;
  }

  async userData() {
    return await this.storage.get('userData');
  }

  async getCompanyId() {
    return await this.storage.get('companyId');
  }

  async getScanBoardUrl() {
    return await this.storage.get('scanBoardUrl');
  }

  async authStateSet() {
    this.authenticationState.next(true);
  }

  async userName() {
    return await this.storage.get('username');
  }
}
